import React, { ReactElement, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { ChevronDown, ChevronUp, Eye, EyeOff, Pencil, Plus, Trash2 } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';

import { Button } from '@ui/components/ui/button';
import { Switch } from '@ui/components/ui/switch';
import { DataTable, DataTableColumnHeader } from '@ui/components/ui/data-table';

import { useOrganizationChapters, useReorderChapters, useUpdateChapter } from '@data/hooks/organization';
import type { Chapter } from '@data/utils/types';
import { useOrganizationId } from '@app/utils/hooks';
import { pageHeaderRowClass, pageTitleClass } from '@app/constants/styles';

import ChapterFormDialog from './ChapterFormDialog';
import DeleteChapterDialog from './DeleteChapterDialog';
import { rowMarker, useRowMoveAnimation } from './useRowMoveAnimation';

const ChaptersAdminPage = (): ReactElement => {
  const { t } = useTranslation();
  // useOrganizationId resolves the id from the pathname even when nested <Routes> drop the param
  // from useParams — without this the page rendered an empty list and posted to /organizations//chapters.
  const organizationId = useOrganizationId() ?? '';

  const {
    data: chapters,
    isLoading,
    isError,
    refetch,
  } = useOrganizationChapters(organizationId, {
    includeDisabled: true,
  });
  const updateChapter = useUpdateChapter(organizationId);
  const reorderChapters = useReorderChapters(organizationId);

  // null = closed, 'create' = create dialog, Chapter = edit dialog with that row
  const [formState, setFormState] = useState<'create' | Chapter | null>(null);
  const [deleting, setDeleting] = useState<Chapter | null>(null);
  // Retired chapters are hidden on arrival — the list is about the chapters people are in. One click
  // brings them back when an admin needs to re-enable or delete one.
  const [hideDeactivated, setHideDeactivated] = useState(true);
  // Inline error shown when the isActive toggle is rejected — most often the API refusing to
  // disable the last active chapter. Without this the switch silently snaps back and the admin
  // can't tell why.
  const [toggleError, setToggleError] = useState<string | null>(null);

  // Filtered, not refetched: the fetch above asks for includeDisabled, so the retired rows are already here.
  const rows = useMemo(() => chapters?.items ?? [], [chapters]);
  const visibleRows = useMemo(
    () => (hideDeactivated ? rows.filter(chapter => chapter.isActive) : rows),
    [rows, hideDeactivated],
  );
  const deactivatedCount = rows.length - rows.filter(chapter => chapter.isActive).length;

  const tableRef = useRef<HTMLDivElement>(null);
  const [movedId, setMovedId] = useState<string | null>(null);
  useRowMoveAnimation(tableRef, visibleRows.map(row => row.id).join(), movedId);

  /** Swaps with the neighbour on screen, so a hidden retired chapter never costs a click that seems to do nothing. */
  const move = (chapter: Chapter, step: -1 | 1): void => {
    const neighbour = visibleRows[visibleRows.findIndex(row => row.id === chapter.id) + step];
    if (!neighbour) return;
    const order = rows.map(row => row.id);
    const from = order.indexOf(chapter.id);
    const to = order.indexOf(neighbour.id);
    [order[from], order[to]] = [order[to], order[from]];
    setMovedId(chapter.id);
    reorderChapters.mutate(order);
  };

  /* eslint-disable react/no-unstable-nested-components -- TanStack column callbacks are render fns, not components */
  const columns: ColumnDef<Chapter, unknown>[] = useMemo(
    () => [
      {
        accessorKey: 'key',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('chaptersAdmin.columns.key')} />,
        cell: ({ row }) => (
          <span className={`font-mono text-sm ${row.original.isActive ? '' : 'text-muted-foreground'}`}>
            {row.original.key}
          </span>
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'label',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('chaptersAdmin.columns.label')} />,
        cell: ({ row }) => (
          <span className={row.original.isActive ? '' : 'text-muted-foreground'}>{row.original.label}</span>
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'memberCount',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('chaptersAdmin.columns.members')} />,
        cell: ({ row }) => (
          <span className={`tabular-nums ${row.original.memberCount ? '' : 'text-muted-foreground'}`}>
            {row.original.memberCount ?? 0}
          </span>
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'isActive',
        header: t('chaptersAdmin.columns.active'),
        cell: ({ row }) => (
          <Switch
            checked={row.original.isActive}
            disabled={updateChapter.isPending}
            onCheckedChange={checked => {
              // Clear any stale error from a previous failed toggle so the banner doesn't linger
              // over the pending state of this new request.
              setToggleError(null);
              updateChapter.mutate(
                { chapterId: row.original.id, values: { isActive: checked } },
                {
                  onError: err => {
                    const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
                    setToggleError(message ?? t('chaptersAdmin.form.saveFailed'));
                  },
                },
              );
            }}
            aria-label={t('chaptersAdmin.columns.active')}
          />
        ),
        enableSorting: false,
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">{t('chaptersAdmin.columns.actions')}</span>,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1" {...rowMarker(row.original.id)}>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={row.index === 0}
              onClick={() => move(row.original, -1)}
              aria-label={t('chaptersAdmin.actions.moveUp', { label: row.original.label })}
            >
              <ChevronUp className="size-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={row.index === visibleRows.length - 1}
              onClick={() => move(row.original, 1)}
              aria-label={t('chaptersAdmin.actions.moveDown', { label: row.original.label })}
            >
              <ChevronDown className="size-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setFormState(row.original)}
              aria-label={t('chaptersAdmin.actions.edit')}
            >
              <Pencil className="size-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setDeleting(row.original)}
              aria-label={t('chaptersAdmin.actions.delete')}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ),
        enableSorting: false,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- move reads the rows it is drawn with
    [t, updateChapter, visibleRows],
  );
  /* eslint-enable react/no-unstable-nested-components */

  return (
    <>
      <Helmet>
        <title>{t('chaptersAdmin.title')}</title>
      </Helmet>

      {/* No padding of its own: the app layout already provides the page gutter, so the title and the
          table line up with Insights and Clients instead of sitting 24px inside them. */}
      <div className="flex w-full min-w-0 flex-col gap-5">
        {/* Title alone in the row, so nothing taller than the row's floor can push it off the line every
            other page sits on. Stacks below md, like the Clients and Products headers. */}
        <div className={`${pageHeaderRowClass} flex flex-col gap-4 md:flex-row md:items-center md:justify-between`}>
          <h1 className={pageTitleClass}>{t('chaptersAdmin.title')}</h1>
          <div className="flex items-center gap-2">
            {deactivatedCount > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-pressed={hideDeactivated}
                onClick={() => {
                  setMovedId(null);
                  setHideDeactivated(current => !current);
                }}
              >
                {hideDeactivated ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                {hideDeactivated
                  ? t('chaptersAdmin.actions.showDeactivated', { count: deactivatedCount })
                  : t('chaptersAdmin.actions.hideDeactivated', { count: deactivatedCount })}
              </Button>
            )}
            <Button type="button" size="sm" onClick={() => setFormState('create')}>
              <Plus className="size-4" />
              {t('chaptersAdmin.actions.create')}
            </Button>
          </div>
        </div>

        {isError && (
          <div className="flex items-center gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-4">
            <p className="text-sm text-destructive">{t('chaptersAdmin.loadError')}</p>
            <Button type="button" variant="outline" size="sm" onClick={() => refetch()}>
              {t('chaptersAdmin.retry')}
            </Button>
          </div>
        )}

        {toggleError ? (
          <p role="alert" className="mb-3 text-sm text-destructive">
            {toggleError}
          </p>
        ) : null}

        {!isError && (
          <div ref={tableRef}>
            <DataTable<Chapter>
              columns={columns}
              data={visibleRows}
              isLoading={isLoading}
              getRowId={row => row.id}
              emptyMessage={t('chaptersAdmin.empty')}
              padded
            />
          </div>
        )}
      </div>

      <ChapterFormDialog
        organizationId={organizationId}
        chapter={formState === 'create' || formState === null ? null : formState}
        open={formState !== null}
        onClose={() => setFormState(null)}
      />

      <DeleteChapterDialog
        organizationId={organizationId}
        chapter={deleting}
        open={deleting !== null}
        onClose={() => setDeleting(null)}
      />
    </>
  );
};

export default ChaptersAdminPage;
